import { useState, useEffect, useCallback } from 'react';
import { X, Trash2, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import type { Task, TaskExecutionLog } from '../../types/tasks';
import { AGENTS, DEPARTMENTS, getAgentBySlug, getAgentColor } from '../../utils/agents';
import { useTaskStore } from '../../stores/taskStore';

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'] as const;
const CADENCE_OPTIONS = ['daily', 'weekly', 'biweekly', 'monthly', 'quarterly', 'trigger'] as const;

const STATUS_OPTIONS = [
  { key: 'backlog' as const, label: 'Backlog' },
  { key: 'in_progress' as const, label: 'In Progress' },
  { key: 'completed' as const, label: 'Completed' },
];

interface TaskDetailModalProps {
  task: Task;
  onClose: () => void;
}

export default function TaskDetailModal({ task, onClose }: TaskDetailModalProps) {
  const updateTask = useTaskStore((s) => s.updateTask);
  const deleteTask = useTaskStore((s) => s.deleteTask);
  const fetchExecutionLogs = useTaskStore((s) => s.fetchExecutionLogs);

  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || '');
  const [status, setStatus] = useState<string>(task.status);
  const [priority, setPriority] = useState<string>(task.priority);
  const [cadence, setCadence] = useState(task.cadence || '');
  const [assignedAgent, setAssignedAgent] = useState(task.assigned_agent || '');
  const [department, setDepartment] = useState(task.department || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Execution logs
  const [logs, setLogs] = useState<TaskExecutionLog[]>([]);
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [copiedLogIdx, setCopiedLogIdx] = useState<number | null>(null);

  const agent = assignedAgent ? getAgentBySlug(assignedAgent) : null;
  const agentColor = agent ? getAgentColor(agent.department) : 'bg-bg-tertiary';

  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  // Load logs when expanded
  useEffect(() => {
    if (logsExpanded && logs.length === 0) {
      setLogsLoading(true);
      fetchExecutionLogs(task.id).then((data) => {
        setLogs(data);
        setLogsLoading(false);
      });
    }
  }, [logsExpanded, logs.length, fetchExecutionLogs, task.id]);

  function handleAgentChange(slug: string) {
    setAssignedAgent(slug);
    if (slug) {
      const a = AGENTS.find((ag) => ag.slug === slug);
      if (a) setDepartment(a.department);
    }
  }

  async function handleSave() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await updateTask(task.id, {
        title: title.trim(),
        description: description.trim() || null,
        status: status as Task['status'],
        priority: priority as Task['priority'],
        cadence: (cadence || null) as Task['cadence'],
        assigned_agent: assignedAgent || null,
        department: department || null,
      });
      onClose();
    } catch (err) {
      console.error('Failed to update task:', err);
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await deleteTask(task.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  }

  function formatTimestamp(ts: string | null): string {
    if (!ts) return '-';
    return format(new Date(ts), 'MMM d, yyyy h:mm a');
  }

  async function copyLog(logText: string, idx: number) {
    await navigator.clipboard.writeText(logText);
    setCopiedLogIdx(idx);
    setTimeout(() => setCopiedLogIdx(null), 2000);
  }

  // Build activity timeline from task data
  const activity = [];
  activity.push({
    type: 'created',
    description: 'Task created',
    timestamp: task.created_at,
  });
  if (task.assigned_agent) {
    const agentData = getAgentBySlug(task.assigned_agent);
    activity.push({
      type: 'assigned',
      description: `Assigned to ${agentData?.name || task.assigned_agent}`,
      timestamp: task.created_at,
    });
  }
  if (task.status === 'in_progress') {
    activity.push({
      type: 'status_change',
      description: 'Status changed — In Progress',
      timestamp: task.updated_at,
    });
  }
  if (task.status === 'completed' && task.completed_at) {
    const agentData = task.assigned_agent ? getAgentBySlug(task.assigned_agent) : null;
    activity.push({
      type: 'completed',
      description: `Completed${agentData ? ` by ${agentData.name}` : ''}`,
      timestamp: task.completed_at,
    });
  }
  activity.reverse();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-bg-secondary border border-border-subtle rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-text-primary">Task Details</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col gap-5">
          {/* Agent Banner */}
          <div className="flex items-center gap-3 bg-bg-tertiary/50 rounded-lg p-3">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-medium text-white ${agentColor}`}>
              {agent ? agent.name.charAt(0) : '?'}
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">
                {agent?.name || 'Unassigned'}
              </p>
              {agent?.title && (
                <p className="text-xs text-text-muted">{agent.title}</p>
              )}
            </div>
            <div className="ml-auto flex gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setStatus(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    status === opt.key
                      ? 'bg-accent-purple text-white'
                      : 'bg-bg-tertiary text-text-muted hover:text-text-secondary'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title + Description */}
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple resize-none"
              rows={3}
            />
          </div>

          {/* Fields grid */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Cadence</label>
              <select
                value={cadence}
                onChange={(e) => setCadence(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {CADENCE_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Assigned Agent</label>
            <select
              value={assignedAgent}
              onChange={(e) => handleAgentChange(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
            >
              <option value="">Unassigned</option>
              {AGENTS.map((a) => (
                <option key={a.slug} value={a.slug}>
                  {a.name} — {a.title}
                </option>
              ))}
            </select>
          </div>

          {/* Timestamps */}
          <div className="border-t border-border-subtle pt-3">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <span className="text-text-muted block">Created</span>
                <span className="text-text-secondary">{formatTimestamp(task.created_at)}</span>
              </div>
              <div>
                <span className="text-text-muted block">Updated</span>
                <span className="text-text-secondary">{formatTimestamp(task.updated_at)}</span>
              </div>
              <div>
                <span className="text-text-muted block">Completed</span>
                <span className="text-text-secondary">{formatTimestamp(task.completed_at)}</span>
              </div>
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="border-t border-border-subtle pt-3">
            <h3 className="text-sm font-medium text-text-primary mb-3">Activity</h3>
            <div className="flex flex-col gap-2">
              {activity.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${
                    item.type === 'completed' ? 'bg-success' :
                    item.type === 'status_change' ? 'bg-info' :
                    item.type === 'assigned' ? 'bg-accent-purple' :
                    'bg-text-muted'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-text-secondary">{item.description}</span>
                  </div>
                  <span className="text-xs text-text-muted shrink-0">
                    {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Execution Logs (collapsible) */}
          <div className="border-t border-border-subtle pt-3">
            <button
              onClick={() => setLogsExpanded(!logsExpanded)}
              className="flex items-center gap-2 text-sm font-medium text-text-primary hover:text-accent-purple-light transition-colors"
            >
              {logsExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Execution Logs
            </button>

            {logsExpanded && (
              <div className="mt-3">
                {logsLoading ? (
                  <p className="text-xs text-text-muted">Loading logs...</p>
                ) : logs.length === 0 ? (
                  <p className="text-xs text-text-muted">No execution logs found for this task.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {logs.map((log, i) => (
                      <div key={i} className="bg-bg-tertiary rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-text-muted">{log.job_id}</span>
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              log.status === 'success' ? 'bg-success/15 text-success' :
                              log.status === 'error' ? 'bg-error/15 text-error' :
                              'bg-bg-tertiary text-text-muted'
                            }`}>
                              {log.status}
                            </span>
                            {log.duration_ms > 0 && (
                              <span className="text-text-muted">{(log.duration_ms / 1000).toFixed(1)}s</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-text-muted">
                              {format(new Date(log.timestamp), 'MMM d, h:mm a')}
                            </span>
                            <button
                              onClick={() => copyLog(log.result_full, i)}
                              className="p-1 rounded hover:bg-bg-secondary text-text-muted hover:text-text-secondary transition-colors"
                              title="Copy log"
                            >
                              {copiedLogIdx === i ? (
                                <Check className="h-3.5 w-3.5 text-success" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                        </div>
                        {log.result_summary && (
                          <p className="text-xs text-text-secondary mb-2">{log.result_summary}</p>
                        )}
                        {log.result_full && (
                          <pre className="text-xs text-text-muted font-mono bg-bg-primary rounded p-2 overflow-x-auto max-h-40 overflow-y-auto">
                            {log.result_full}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center mt-2">
            <button
              onClick={handleDelete}
              className={`flex items-center gap-1.5 text-sm transition-colors px-3 py-2 rounded-md ${
                confirmDelete
                  ? 'bg-error/15 text-error'
                  : 'text-text-muted hover:text-error'
              }`}
            >
              <Trash2 className="h-4 w-4" />
              {confirmDelete ? 'Confirm Delete' : 'Delete'}
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="text-text-muted hover:text-text-secondary text-sm transition-colors px-3 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !title.trim()}
                className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
